/**
 * Branding Configuration Converters
 *
 * Helper functions to convert brand configuration to/from
 * legacy formats used by journey and email systems.
 *
 * @deprecated These converters may not be actively used. Consider using
 * brandConfigToDesignBaseSettings from @/lib/design/brand-config instead.
 */

import { toBcp47 } from '@ophelos/shared/utils/languages';
import { brandConfigToDesignBaseSettings } from '@/lib/design/brand-config';
import type { BrandConfiguration } from '@/lib/types/brand-config';
import type { DesignBaseSettings } from '@/types';

/**
 * Journey Configuration structure matching customer-portal types
 * This is a local type definition to avoid cross-app dependencies
 */
export interface JourneyConfiguration {
  payment: {
    allowPartialPayment: boolean;
    partialPaymentTab: boolean;
    allowFullPayLater: boolean;
    allowPartialPayLater: boolean;
    enablePaymentPlan: boolean;
    paymentPlan?: {
      minEligibleDebt?: number;
      tiers?: Array<{
        id: string;
        debtThreshold: number;
        maxDurationMonths: number;
        minInstallmentAmount?: number;
        label?: string;
      }>;
      maxDurationMonths?: number;
    };
  };
  ui: {
    branding: {
      theme: 'professional' | 'approachable';
      showGradientSphere?: boolean;
      logo?: {
        url?: string;
        light?: string;
        dark?: string;
        alt?: string;
      };
      logoIcon?: {
        light?: string;
        dark?: string;
      };
      favicon?: {
        url: string;
      };
      colors: {
        primary: string;
        primaryForeground?: string;
        buttonBackground?: {
          light?: string;
          dark?: string;
        };
        buttonText?: {
          light?: string;
          dark?: string;
        };
      };
    };
    language: string;
    availableLanguages?: string[];
    features: {
      showMergeAccounts: boolean;
      showPaymentHistory: boolean;
      showDisputes: boolean;
    };
    termsLinks?: Array<{
      name: string | Record<string, string>; // Can be simple string or localized object keyed by BCP47 locale
      url: string;
    }>;
  };
}

/**
 * Convert BrandConfiguration to JourneyConfiguration
 * Maps brand config fields to the journey configuration structure expected by customer portal
 */
export function brandConfigToJourneyConfig(brandConfig: BrandConfiguration): JourneyConfiguration {
  // Determine logo URLs for light and dark mode - ONLY use logo_full
  const logoLight = brandConfig.logo_full?.light || undefined;
  const logoDark = brandConfig.logo_full?.dark || undefined;

  // Determine logo icon URLs for light and dark mode - ONLY use logo_icon
  const logoIconLight = brandConfig.logo_icon?.light || undefined;
  const logoIconDark = brandConfig.logo_icon?.dark || undefined;

  // Determine favicon URL - prefer light mode, fallback to dark
  const faviconUrl = brandConfig.favicon?.light || brandConfig.favicon?.dark || undefined;

  // Convert payment plan tiers if they exist
  const paymentPlanTiers =
    brandConfig.paymentPlanTiers?.map((tier, index) => ({
      id: tier.id || `tier_${index}`,
      debtThreshold: tier.minimumAmount || 0,
      maxDurationMonths: tier.maxInstallments || 6,
      minInstallmentAmount: tier.minimumAmount,
      label: tier.name || tier.description,
    })) || [];

  return {
    payment: {
      allowPartialPayment: brandConfig.payment?.allowPartialPayment ?? true,
      partialPaymentTab: true, // Default to true, can be configured later
      allowFullPayLater: true, // Default to true, can be configured later
      allowPartialPayLater: true, // Default to true, can be configured later
      enablePaymentPlan: brandConfig.payment?.enablePaymentPlan ?? true,
      paymentPlan:
        paymentPlanTiers.length > 0
          ? {
              minEligibleDebt: 0,
              tiers: paymentPlanTiers,
            }
          : undefined,
    },
    ui: {
      branding: {
        theme: brandConfig.theme || 'professional',
        // Show gradient sphere only for approachable theme, always false for professional
        showGradientSphere: brandConfig.theme === 'professional' ? false : (brandConfig.showGradientSphere ?? true),
        logo:
          logoLight || logoDark
            ? {
                url: logoLight || logoDark || undefined, // Keep for backward compatibility
                light: logoLight,
                dark: logoDark,
                alt: brandConfig.logo?.alt || brandConfig.company_name || undefined,
              }
            : undefined,
        logoIcon:
          logoIconLight || logoIconDark
            ? {
                light: logoIconLight,
                dark: logoIconDark,
              }
            : undefined,
        favicon: faviconUrl
          ? {
              url: faviconUrl,
            }
          : undefined,
        colors: {
          primary: brandConfig.colors?.brand || '#7f53dd', // Keep for backward compatibility
          primaryForeground: brandConfig.colors?.buttonText?.light || '#FFFFFF', // Keep for backward compatibility
          buttonBackground: brandConfig.colors?.buttonBackground,
          buttonText: brandConfig.colors?.buttonText,
        },
      },
      language: toBcp47(brandConfig.defaultLanguage),
      availableLanguages: brandConfig.availableLanguages?.map(toBcp47),
      features: {
        showMergeAccounts: brandConfig.uiFeatures?.showAccountBalance ?? true,
        showPaymentHistory: brandConfig.uiFeatures?.showPaymentHistory ?? true,
        showDisputes: brandConfig.uiFeatures?.showDisputes ?? true,
      },
      // Preserve localized name object for runtime locale selection in customer portal
      termsLinks: brandConfig.journey_footer_links?.map((link) => ({
        name: link.name, // Keep as string or localized object
        url: link.url,
      })),
    },
  };
}

/**
 * Convert brand config to journey UI branding format
 *
 * @deprecated This function may not be actively used. Journey UI configuration
 * should be handled directly by the customer portal using BrandConfiguration.
 */
export function brandingToJourneyUI(branding: BrandConfiguration): {
  theme: 'professional' | 'approachable';
  logo?: { url: string; alt?: string };
  favicon?: { light?: string; dark?: string };
  colors: {
    primary: string;
    primaryForeground?: string;
  };
} {
  return {
    theme: branding.theme || 'professional',
    logo: branding.logo,
    favicon: branding.favicon,
    colors: {
      primary: branding.colors?.brand || '#7f53dd',
      primaryForeground: branding.colors?.buttonText?.light || '#FFFFFF',
    },
  };
}

/**
 * Convert brand config to email design base settings format
 *
 * @deprecated Use brandConfigToDesignBaseSettings from @/lib/design/brand-config instead.
 * This function is kept for backward compatibility but delegates to the newer function.
 */
export function brandingToEmailGlobalSettings(branding: BrandConfiguration | null): DesignBaseSettings {
  // Delegate to the current implementation
  return brandConfigToDesignBaseSettings(branding);
}
